import dayjs from "dayjs";
import Encoding from "encoding-japanese";

// See documentation: https://www.npmjs.com/package/encoding-japanese
export const convertToJis = (csvString: string) => {
  const unicodeArray = Encoding.stringToCode(csvString);
  const sjisArray = Encoding.convert(unicodeArray, {
    to: "SJIS",
    from: "UNICODE",
    type: "array",
  });
  return Encoding.urlEncode(sjisArray);
};

export interface CsvFilenameParams {
  currentDate?: string; // YYYY-MM-DD, defaults to today
  startDate?: string; // YYYY-MM-DD
  endDate?: string; // YYYY-MM-DD
  organizationName?: string;
  organizationId?: number | string;
  facilityName?: string;
  facilityId?: number | string;
  contentDescription: string; // Required - will be translated
}

/**
 * Build a standardized CSV filename using the hierarchical structure:
 * CurrentDate_StartDate-EndDate_OrganizationName_FacilityName_ContentDescription
 * Parts that are not applicable are skipped.
 * Note: organizationId and facilityId are kept in the interface for CSV content generation
 * but are NOT included in the filename.
 */
export const buildCsvFilename = (
  params: CsvFilenameParams,
  t?: (key: string) => string
): string => {
  const parts: string[] = [];
  // Current date (always include)
  const currentDate = params.currentDate || dayjs().format("YYYY-MM-DD");
  parts.push(currentDate);

  // Date range - ensure YYYY-MM-DD format only
  if (params.startDate && params.endDate) {
    // Extract only the date part if timestamp is included
    const startDateOnly = params.startDate.split("T")[0];
    const endDateOnly = params.endDate.split("T")[0];

    if (startDateOnly === endDateOnly) {
      parts.push(startDateOnly);
    } else {
      parts.push(`${startDateOnly}_${endDateOnly}`);
    }
  } else if (params.startDate) {
    parts.push(params.startDate.split("T")[0]);
  } else if (params.endDate) {
    parts.push(params.endDate.split("T")[0]);
  }

  // Organization (name only, no ID)
  if (params.organizationName) {
    // Sanitize organization name for filename
    const sanitizedOrgName = params.organizationName
      .replace(/[/\\:*?"<>|]/g, "_")
      .replace(/\s+/g, "_");
    parts.push(sanitizedOrgName);
  }

  // Facility (name only, no ID)
  if (params.facilityName) {
    // Sanitize facility name for filename
    const sanitizedFacName = params.facilityName
      .replace(/[/\\:*?"<>|]/g, "_")
      .replace(/\s+/g, "_");
    parts.push(sanitizedFacName);
  }

  // Content description (translate if possible, otherwise use raw value)
  let translatedContent = params.contentDescription;
  if (t && typeof t === "function") {
    try {
      // Try with csvFilename prefix first
      const keyWithPrefix = params.contentDescription.startsWith("csvFilename.")
        ? params.contentDescription
        : `csvFilename.${params.contentDescription}`;

      const translated = t(keyWithPrefix);
      // Only use translation if it's not empty and not the same as the key
      if (
        translated &&
        translated !== keyWithPrefix &&
        translated !== params.contentDescription
      ) {
        translatedContent = translated;
      }
    } catch (error) {
      console.warn(
        "Translation failed for content description, using raw value:",
        error
      );
    }
  }
  parts.push(translatedContent);

  return parts.join("_");
};

/**
 * Insert an ID column to the left of a name column in a CSV string
 * @param csvString The CSV content as a string
 * @param nameColumnHeader The header name to find (e.g., "Facility Name")
 * @param idColumnHeader The new ID column header (e.g., "Facility ID")
 * @param idMapping Map from name to ID
 */
export const insertIdColumnInCsv = (
  csvString: string,
  nameColumnHeader: string,
  idColumnHeader: string,
  idMapping: Map<string, string | number>
): string => {
  const lines = csvString.split("\n").filter((line) => line.trim());
  if (lines.length === 0) return csvString;

  const headerLine = lines[0];
  const headers = headerLine.split(",").map((h) => h.trim().replace(/"/g, ""));
  const nameIndex = headers.findIndex(
    (h) => h.toLowerCase() === nameColumnHeader.toLowerCase()
  );

  if (nameIndex === -1) return csvString; // Column not found

  // Insert ID header
  headers.splice(nameIndex, 0, idColumnHeader);
  const newLines = [headers.map((h) => `"${h}"`).join(",")];

  // Process data rows
  for (let i = 1; i < lines.length; i++) {
    const cells = lines[i].split(",").map((c) => c.trim().replace(/"/g, ""));
    const name = cells[nameIndex] || "";
    const id = idMapping.get(name) || "";
    cells.splice(nameIndex, 0, String(id));
    newLines.push(cells.map((c) => `"${c}"`).join(","));
  }

  return newLines.join("\n");
};

/**
 * Insert ID columns for both Organization and Facility if present in CSV
 * If Organization Name column doesn't exist, add Organization ID as first column
 */
export const insertOrgAndFacilityIds = (
  csvString: string,
  orgMapping?: Map<string, string | number>,
  facilityMapping?: Map<string, string | number>
): string => {
  let result = csvString;

  // Insert Organization ID (if applicable)
  if (orgMapping && orgMapping.size > 0) {
    // Try to insert next to existing Organization Name column
    const afterInsertion = insertIdColumnInCsv(
      result,
      "Organization Name",
      "Organization ID",
      orgMapping
    );

    // If insertion was successful (column existed), use the result
    if (afterInsertion !== result) {
      result = afterInsertion;
    } else {
      // If Organization Name column doesn't exist, add Organization ID at the beginning
      const lines = result.split("\n").filter((line) => line.trim());
      if (lines.length > 0 && orgMapping.size > 0) {
        const [, orgId] = Array.from(orgMapping.entries())[0];
        const newLines: string[] = [];

        // Add headers
        const headerLine = lines[0];
        newLines.push(`"Organization ID",${headerLine}`);

        // Add data rows with org ID
        for (let i = 1; i < lines.length; i++) {
          newLines.push(`"${orgId}",${lines[i]}`);
        }

        result = newLines.join("\n");
      }
    }
  }

  // Then insert Facility ID (if applicable)
  if (facilityMapping && facilityMapping.size > 0) {
    const facilityHeaders = [
      "Facility Name",
      "Location Name",
      "店舗名",
      "施設名",
    ];
    for (const header of facilityHeaders) {
      const testResult = insertIdColumnInCsv(
        result,
        header,
        "Facility ID",
        facilityMapping
      );
      if (testResult !== result) {
        result = testResult;
        break;
      }
    }
  }

  return result;
};

export const downloadCsv = (
  rows: string[],
  filename: string,
  forWindows: boolean
) => {
  const lineBreakChar = forWindows ? "\r\n" : "\n";
  const csvContent = rows.join(lineBreakChar);

  let convertedCsvContent: string;
  if (forWindows) {
    convertedCsvContent = convertToJis(csvContent);
  } else {
    convertedCsvContent = encodeURI(csvContent);
  }

  const encodedUri = `data:text/csv;charset=${
    forWindows ? "Shift_JIS" : "utf-8"
  },\uFEFF${convertedCsvContent}`;

  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute(
    "download",
    `${filename}${forWindows ? "(日本語文字化け対応)" : ""}.csv`
  );
  document.body.appendChild(link);
  link.click();
};
